import { ReactNode, useCallback, useMemo, useState } from 'react';
import { css, cx } from '@emotion/css';
import { Button, Popover, Tag, Tooltip } from 'antd';
import { useFocused, useSelected } from 'slate-react';
import { ConceptElement } from '../slateTypes';
import { useKnowledgeContext } from '@knowledge/knowledges/general/KnowledgeContext';
import { FormOutlined, LoadingOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import { What } from 'views/Marketing/components/PolicyTable';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';

const styles = {
  container: css`
    display: inline-block;
    margin: 0 4px;
    cursor: pointer;
  `,
  tag: css`
    background-color: var(--fett-bg-app);
    border: 0;
    color: var(--fett-text-secondary);
    margin: 0;
    /* light 实底 #E6E4F2（antd color prop 内联），dark 改 UED Purple 填充浅色型 */
    [data-color-mode='dark'] & {
      background-color: var(--fett-tag-purple-bg) !important;
      color: var(--fett-tag-purple-text) !important;
    }
  `,
  selected: css`
    box-shadow: 0 0 0 3px #b4d5ff;
  `,
};

interface Props {
  element: any;
  attributes: any;
  children: any;
  language?: General.Language;
}

const TagConcept = (props: Props) => {
  const { element, attributes, children, language } = props;
  const { label } = element as ConceptElement;
  const focused = useFocused();
  const selected = useSelected();

  const [tipsVisible, setTipsVisible] = useState(false);

  const { getConceptText, conceptTipsLabel } = useConceptText(language);

  const conceptText = getConceptText(label);

  const conceptTips = conceptTipsLabel[label as keyof typeof conceptTipsLabel];

  const concept = (
    <div {...attributes} contentEditable={false} className={styles.container}>
      <Tag color="#E6E4F2" className={cx(styles.tag, focused && selected && styles.selected)}>
        {conceptText}
        {conceptTips && (
          <Tooltip visible={tipsVisible} onVisibleChange={setTipsVisible} title={conceptTips}>
            <What />
          </Tooltip>
        )}
      </Tag>
      {children}
    </div>
  );

  if (label !== '商品发货天数') {
    return concept;
  }

  return (
    <KnowledgeTagConcept
      hidePopover={tipsVisible}
      concept={concept}
      knowledgeType="LOGISTICS_SEND_OUT_TIME"
    />
  );
};

export function KnowledgeTagConcept({
  knowledgeType,
  concept,
  hidePopover,
}: {
  knowledgeType: Knowledge.KnowledgeType;
  concept: ReactNode;
  hidePopover?: boolean;
}) {
  const { knowledgeExampleMap, showKnowledgeListMoal, getKnowledgeExample } = useKnowledgeContext();

  const { [knowledgeType]: knowledgeExample } = knowledgeExampleMap;

  useMount(() => {
    getKnowledgeExample(knowledgeType);
  });

  const [visible, setVisible] = useState(false);

  const content = (
    <div>
      {knowledgeExample === false ? <LoadingOutlined /> : knowledgeExample || null}
      <Button
        type="link"
        icon={<FormOutlined />}
        onClick={() => {
          showKnowledgeListMoal(knowledgeType);
          setVisible(false);
        }}
      />
    </div>
  );

  return (
    <Popover visible={visible && !hidePopover} onVisibleChange={setVisible} content={content}>
      {concept}
    </Popover>
  );
}

export function useConceptText(language?: General.Language) {
  const systemLang = useSystemLanguageValue();

  const lang = language || systemLang;

  const getLabel = useCallback(
    (map: Util.PartialRequired<Partial<Record<General.Language, string>>, 'en-US'>) =>
      map[lang] || map['en-US'],
    [lang],
  );

  const conceptLabel = useMemo<Record<Marketing.Concept, string>>(() => {
    return {
      买家昵称: getLabel({
        'zh-CN': '买家昵称',
        'zh-TW': '买家昵称',
        'en-US': 'Buyer Nickname',
        id: 'Nama pembeli',
        th: 'ชื่อลูกค้า',
        tl: 'Buyer Nickname',
        vi: 'Tên người muamua',
        pt: 'Nome de comprador',
      }),
      买家地址: getLabel({
        'zh-CN': '买家地址',
        'zh-TW': '买家地址',
        'en-US': 'Buyer Address',
        id: 'Alamat pembeli',
        th: 'ที่อยู่ผู้ซื้อ',
        tl: 'Buyer Address',
        vi: 'Địa chỉ người mua',
        pt: 'Endereço do comprador',
      }),
      收货地址: getLabel({
        'zh-CN': '收货地址',
        'zh-TW': '收货地址',
        'en-US': 'Delivery Address',
        id: 'Alamat pengiriman',
        th: 'ที่อยู่จัดส่ง',
        tl: 'Delivery Address',
        vi: 'Địa chỉ nhận hàng',
        pt: 'Endereço para envio',
      }),
      订单号: getLabel({
        'zh-CN': '订单号',
        'zh-TW': '订单号',
        'en-US': 'Order No.',
        id: 'Nomor pesanan',
        th: 'หมายเลขคำสั่งซื้อ',
        tl: 'Order No.',
        vi: 'Số thứ tự',
        pt: 'Número do pedido',
      }),
      缺货商品名称: getLabel({
        'zh-CN': '缺货商品名称',
        'zh-TW': '缺货商品名称',
        'en-US': 'Product out of stock',
        id: 'Nama produk Kosong',
        th: 'ชื่อสินค้าที่หมด',
        tl: 'Product out of stock',
        vi: 'Tên sản phẩm hết hàng',
        pt: 'Títulos fora de estoque',
      }),
      商品名称: getLabel({
        'zh-CN': '商品名称',
        'zh-TW': '商品名称',
        'en-US': 'Product Name',
        id: 'Nama produk',
        th: 'ชื่อสินค้า',
        tl: 'Product Name',
        vi: 'Tên sản phẩm',
        pt: 'Nome de produto',
      }),
      推荐商品说明: getLabel({
        'zh-CN': '推荐商品说明',
        'zh-TW': '推荐商品说明',
        'en-US': 'Product recommended',
        id: 'Produk rekomendasi',
        th: 'สินค้าที่แนะนำ',
        tl: 'Product recommended',
        vi: 'Sản phẩm đề xuất',
        pt: 'Descrições recomendadas do produto',
      }),
      店铺名称: getLabel({
        'zh-CN': '店铺名称',
        'zh-TW': '店铺名称',
        'en-US': 'Store Name',
        id: 'Nama toko',
        th: 'ชื่อร้านค้า',
        tl: 'Pangalan ng Store',
        vi: 'Tên cửa hàng',
        pt: 'Nome da loja',
      }),
      物流单号: getLabel({
        'zh-CN': '物流单号',
        'zh-TW': '物流单号',
        'en-US': 'Tracking Number',
        id: 'Nomor resi pengiriman',
        th: 'เลขพัสดุ',
        tl: 'Tracking Number',
        vi: 'Mã đơn hàng vận chuyển',
        pt: 'Numero de rastreio',
      }),
      当前物流描述: getLabel({
        'zh-CN': '当前物流描述',
        'zh-TW': '当前物流描述',
        'en-US': 'Current logistics status',
        id: 'Status Pengiriman saat ini',
        th: 'สถานะโลจิสติกส์ปัจจุบัน',
        tl: 'Current logistics status',
        vi: 'Trạng thái vận chuyển hiện tại',
        pt: 'Estado da logística atual',
      }),
      券码: getLabel({
        'zh-CN': '券码',
        'zh-TW': '券码',
        'en-US': 'Claim code',
        id: 'Kode Klaim',
        th: 'รหัสบัตร',
        tl: 'Claim code',
        vi: 'Mã',
        pt: 'Claim code',
      }),
      好评: getLabel({
        'zh-CN': '好评',
        'zh-TW': '好评',
        'en-US': 'Positive Review',
        id: 'Penilaian Positif',
        th: 'รีวิวที่ดี',
        tl: 'Positive Review',
        vi: 'Đánh giá tốt',
        pt: 'positivo comentário',
      }),
      差评: getLabel({
        'zh-CN': '差评',
        'zh-TW': '差评',
        'en-US': 'Negative Review',
        id: 'Penilaian Negatif',
        th: 'รีวิวที่ไม่ดี',
        tl: 'Negative Review',
        vi: 'Đánh giá xấu',
        pt: 'negativo comentário',
      }),
      商品发货天数: getLabel({
        'zh-CN': '商品发货天数',
        'zh-TW': '商品发货天数',
        'en-US': 'Product shipping time',
        id: 'Waktu pengiriman produk',
        th: 'ระยะเวลาการจัดส่ง',
        tl: 'Oras ng Product Shipment',
        vi: 'Số ngày gửi hàng đi',
        pt: 'Número de dias para envio do produto',
      }),
      商品折扣: getLabel({
        'zh-CN': '商品折扣',
        'zh-TW': '商品折扣',
        'en-US': 'Product discount',
        id: 'Diskon Produk',
        th: 'ส่วนลดสินค้า',
        tl: 'Product discount',
        vi: 'Chiết khấu sản phẩm',
        pt: 'Desconto de produto',
      }),
      直播时间段: getLabel({
        'zh-CN': '直播时间段',
        'zh-TW': '直播时间段',
        'en-US': 'Live time period',
        id: 'Periode waktu Live',
        th: 'ช่วงเวลาไลฟ์',
        tl: 'Oras ng live',
        vi: 'Khoảng thời gian livestream',
        pt: 'Período ao vivo',
      }),
      退款金额: getLabel({
        'zh-CN': '退款金额',
        'zh-TW': '退款金额',
        'en-US': 'Amount',
        id: 'Dana pengembalian',
        th: 'จำนวนเงินคืน',
        tl: 'Amount',
        vi: 'Số tiền hoàn trả',
        pt: 'Valor do reembolso',
      }),
      使用方法: getLabel({
        'zh-CN': '使用方法',
        'zh-TW': '使用方法',
        'en-US': 'Use method',
        id: 'Cara penggunaan',
        th: 'วิธีการใช้',
        tl: 'Paano gamitin',
        vi: 'Phương pháp sử dụng',
        pt: 'Método de uso',
      }),
      优惠券面值: getLabel({
        'zh-CN': '优惠券面值',
        'zh-TW': '优惠券面值',
        'en-US': 'Coupon value',
        id: 'Nilai kupon',
        th: 'มูลค่าคูปอง',
        tl: 'Halaga ng coupon',
        vi: 'Giá trị của phiếu giảm giá',
        pt: 'Valor de cupom',
      }),
      优惠券时限: getLabel({
        'zh-CN': '优惠券时限',
        'zh-TW': '优惠券时限',
        'en-US': 'Coupon time limit',
        id: 'Batas waktu Voucher',
        th: 'คูปองจำกัดเวลา',
        tl: 'Limit ng coupon',
        vi: 'Thời hạn của phiếu giảm giá',
        pt: 'Limite de tempo de cupom',
      }),
      补偿金额: getLabel({
        'zh-CN': '补偿金额',
        'zh-TW': '补偿金额',
        'en-US': 'Compensation amount',
        id: 'Jumlah kompensasi',
        th: 'จำนวนเงินชดเชย',
        tl: 'Halaga ng kompensasyon',
        vi: 'Số tiền bồi thường',
        pt: 'Montante de compensação',
      }),
      补偿方式: getLabel({
        'zh-CN': '补偿方式',
        'zh-TW': '补偿方式',
        'en-US': 'Compensation method',
        id: 'Metode kompensasi',
        th: 'วิธีการชดเชย',
        tl: 'Paraan ng kompensasyon',
        vi: 'Phương thức bồi thường',
        pt: 'Método de compensação',
      }),
      发货时间: getLabel({
        'zh-CN': '发货时间',
        'zh-TW': '发货时间',
        'en-US': 'Shipping time',
        id: 'Waktu pengantaran',
        th: 'เวลาจัดส่ง',
        tl: 'Oras ng shipping',
        vi: 'Thời gian gửi hàng',
        pt: 'Tempo de envio',
      }),
      取消原因: getLabel({
        'zh-CN': '取消原因',
        'zh-TW': '取消原因',
        'en-US': 'Cancellation Reason',
        id: 'Alasan pembatalan',
        th: 'สาเหตุที่ยกเลิก',
        tl: 'Dahilan ng Pagkansela',
        vi: 'Lý do hủy',
        pt: 'Motivo de cancelamento',
      }),
      拒绝原因: getLabel({
        'zh-CN': '拒绝原因',
        'zh-TW': '拒绝原因',
        'en-US': 'Reason for rejection',
        id: 'Alasan Penolakan',
        th: 'เหตุผลในการปฏิเสธ',
        tl: 'Reason ng rejection',
        vi: 'Lý do từ chối',
        pt: 'Razão da rejeição',
      }),
    };
  }, [getLabel]);

  const conceptTipsLabel = useMemo<
    Partial<Record<Marketing.Concept | Todo.Concept, string>>
  >(() => {
    return {
      商品折扣: getLabel({
        'zh-CN': '小乐将在发送消息时，实时获取当前商品的折扣比例，以促使买家尽快下单购买',
        'zh-TW': '小乐将在发送消息时，实时获取当前商品的折扣比例，以促使买家尽快下单购买',
        'en-US':
          'We will obtain the current discount  for the product instantly when sending messages, encouraging customers to place orders promptly',
        id: 'Saat akan mengirim pesan, kami akan segera memperoleh dan menyampaikan informasi diskon untuk produk tersebut, sehingga customer dapat segera melakukan proses checkout.',
        th: 'เสี่ยวเล่อจะได้รับอัตราส่วนส่วนลดของสินค้าปัจจุบันแบบเรียลไทม์เมื่อส่งข้อความเพื่อกระตุ้นให้ผู้ซื้อสั่งซื้อโดยเร็วที่สุด',
        tl: 'Kukunin namin ang current discount para sa product kapag magsesend ng message, na ini-encourage ang mga customers na mag-place ng order agad.',
        vi: 'Chúng tôi sẽ cập nhật thông tin khuyến mãi mới nhất của sản phẩm khi gửi tin nhắn để thúc giục người mua đặt hàng',
        pt: 'Obteremos o desconto atual para o produto instantaneamente ao enviar mensagens, incentivando os clientes a fazer pedidos prontamente',
      }),
      收货地址: getLabel({
        'zh-CN':
          '可发送收货地址以提醒买家检查是否正确，由于平台接口限制，收货地址中部分内容可能以“*”方式显示',
        'zh-TW':
          '可发送收货地址以提醒买家检查是否正确，由于平台接口限制，收货地址中部分内容可能以“*”方式显示',
        'en-US':
          'Delivery address will be sent to remind customers to check if it is correct. Due to API limitations, some content in the delivery address may be displayed as "*"',
        id: "Alamat pengiriman akan dikirim untuk mengingatkan customer agar memeriksa apakah alamat tersebut sudah benar. Namun, karena batasan API, beberapa bagian alamat pengiriman mungkin ditampilkan sebagai '*'.",
        th: 'ที่อยู่จัดส่งสามารถส่งเพื่อเตือนผู้ซื้อให้ตรวจสอบว่าถูกต้องหรือไม่ เนื่องจากข้อจำกัดของอินเทอร์เฟซแพลตฟอร์ม เนื้อหาบางส่วนในที่อยู่จัดส่งอาจแสดงในรูปแบบของ "*"',
        tl: 'Ang delivery  address ay ipapadala para iremind ang customer na i-check kung tama ito. Dahil sa API limitations, ang ibang content sa delivery address ay pwedeng ma-display na  "*"',
        vi: 'Có thể gửi địa chỉ để nhắc nhở người mua kiểm tra xem có chính xác không. Do hạn chế về giao diện của nền tảng, một phần địa chỉ có thể hiển thị dưới dạng "*"',
        pt: 'O endereço de entrega será enviado para lembrar os clientes para verificar se está correto. Devido a limitações da API, algum conteúdo no endereço de entrega pode ser exibido como "*"',
      }),
      商品发货天数: getLabel({
        'zh-CN': '小乐将在发送消息时，获取当前店铺的商品发货天数信息，将准确的预计发货时间告知买家',
        'zh-TW': '小乐将在发送消息时，获取当前店铺的商品发货天数信息，将准确的预计发货时间告知买家',
        'en-US':
          "We will obtain the current store's product shipping time when sending messages, providing customers with accurate shipping times",
        id: 'Saat akan mengirim pesan, kami akan segera mendapatkan dan menyampaikan informasi mengenai waktu pengiriman produk, sehingga customer mengetahui perkiraan waktu pengirimannya.',
        th: 'เมื่อส่งข้อความ เสี่ยวเล่อจะได้รับข้อมูลวันจัดส่งสินค้าของร้านค้าปัจจุบันและแจ้งให้ผู้ซื้อทราบเวลาจัดส่งโดยประมาณที่ถูกต้อง',
        tl: 'Kukunin namin ang shipping time ng product ng current store kapag magsesend ng message, magbibigay ng accurate na shipping time sa customers',
        vi: 'Chúng tôi sẽ cập nhật thông tin về thời gian gửi hàng của sản phẩm đó để ước tính chính xác về thời gian giao hàng và thông báo cho người mua',
        pt: 'Obteremos o tempo de envio do produto atual da loja ao enviar mensagens, fornecendo aos clientes tempo de envio precisos',
      }),
      物流单号: getLabel({
        'zh-CN':
          '可发送物流单号以供买家自行查询，部分平台存在物流单号推送延迟的情况，可能导致无法获取到物流单号',
        'zh-TW':
          '可发送物流单号以供买家自行查询，部分平台存在物流单号推送延迟的情况，可能导致无法获取到物流单号',
        'en-US':
          'Tracking number will be sent for customers to check independently. Some marketplaces may experience delays in tracking number notifications, which could result in the inability to obtain the tracking number',
        id: 'Resi Pengiriman akan dikirim kepada customer agar dapat diperiksa sendiri. Beberapa platform marketplace mungkin mengalami keterlambatan dalam memberikan pemberitahuan resi, sehingga customer mungkin tidak langsung mendapatkannya.',
        th: 'สามารถส่งหมายเลขคำสั่งซื้อให้ผู้ซื้อสอบถามด้วยตนเองได้ บางแพลตฟอร์มอาจพบความล่าช้าในการกดหมายเลขการขนส่ง ซึ่งอาจส่งผลให้ไม่สามารถรับหมายเลขขนส่งได้',
        tl: 'Ang tracking number ay ipapadala para macheck ng customer independently. Ang ibang marketplace ay nakakaexperience ng delay sa tracking number notifications, ang resulta ay hindi makuha ang tracking number.',
        vi: 'Mã vận đơn sẽ được gửi cho khách hàng để kiểm tra. Một số trường hợp cập nhật mã vận đơn chậm trễ, có thể dẫn đến việc không thể tra được mã vận đơn',
        pt: 'O número de rastreamento será enviado para os clientes verificarem independentemente. Algumas plataformas podem sofrer atrasos nas notificações de número de rastreamento, o que pode resultar na incapacidade de obter o número de rastreamento',
      }),
      当前物流描述: getLabel({
        'zh-CN': '小乐将在发送消息时，实时获取当前订单的最新一条物流描述信息，告知买家最新物流进展',
        'zh-TW': '小乐将在发送消息时，实时获取当前订单的最新一条物流描述信息，告知买家最新物流进展',
        'en-US':
          'We will obtain the latest logistics update for the current order instantly when sending messages, informing customers of the latest logistics progress',
        id: 'Saat akan mengirim pesan, kami akan memperoleh dan menyampaikan informasi terbaru mengenai status pengiriman pesanan kepada customer secara real-time.',
        th: 'เมื่อส่งข้อความ เสี่ยวเล่อจะได้รับข้อมูลคำอธิบายด้านการขนส่งล่าสุดของคำสั่งซื้อปัจจุบันแบบเรียลไทม์ และแจ้งให้ผู้ซื้อทราบถึงความคืบหน้าด้านการขนส่ง',
        tl: 'Magkakaroon kami ng latest update sa logistics para sa current order kapag magsesend ng messages. i-inform ang customer sa latest progress ng logistics.',
        vi: 'Chúng tôi sẽ cập nhật thông tin vận chuyển mới nhất của đơn hàng ngay khi gửi tin nhắn, và thông báo cho khách hàng thông tin mới nhất.',
        pt: 'Obteremos a última atualização logística para o pedido atual instantaneamente ao enviar mensagens, informando os clientes sobre o mais recente progresso logístico',
      }),
    };
  }, [getLabel]);

  const getConceptText = useCallback(
    (concept: string) => {
      return conceptLabel[concept as keyof typeof conceptLabel] || concept;
    },
    [conceptLabel],
  );

  return {
    conceptLabel,
    conceptTipsLabel,
    getConceptText,
  };
}

export default TagConcept;
